import Loader from '@/components/Loader';
import useCategories from '@/hooks/useCategories';
import { Category } from '@/lib/Category/types';
import Analytics from '@/system/Analytics';
import { DiscoverEvents } from '@/system/Analytics/events/DiscoverEvents';
import ArrayUtils from '@/utils/ArrayUtils';
import { Images } from '@/utils/Images';
import { Icon16Chevron } from '@vkontakte/icons';
import clsx from 'clsx';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { twMerge } from 'tailwind-merge';

interface ICategoriesProps {
  className?: string;
  onCategoryClick?: (category: Category) => void;
}

function Categories({ className, onCategoryClick }: ICategoriesProps) {
  const { data: categories, isLoading } = useCategories();
  const router = useRouter();

  const renderListItem = (category: Category, index: number) => {
    // TODO: temporarily hide crypto category
    if (category.name === "Crypto") {
      return null;
    }

    return (
      <li key={category.id}>
        <button
          onClick={() => {
            Analytics.track(DiscoverEvents.CLICK_CATEGORY, {
              categoryName: category.name,
            });
            if (onCategoryClick) {
              onCategoryClick(category);
            } else {
              router.push(`/discover/category/${category.id}`);
            }
          }}
          className={
            'flex w-full justify-between items-center px-4 md:px-6 py-2.5 md:py-3 text-left transition-colors hover:bg-white/5'
          }
        >
          <div className="flex space-x-3 md:space-x-4 w-full">
            {category.image ? (
              <Image
                alt={category.name}
                src={Images.categories[category.image]}
                className="flex-shrink-0 size-10 rounded-full bg-white object-cover object-center"
                width={40}
                height={40}
              />
            ) : (
              <div className="bg-white w-10 h-10 md:w-11 md:h-11 rounded-lg flex-shrink-0" />
            )}
            <div className="flex flex-row justify-between w-full min-w-0">
              <div className="w-3/4 min-w-0">
                <h4 className="truncate text-sm md:text-base">{category.name}</h4>
                <h6 className="text-secondary text-xs md:text-sm">{category.numAssets || 0} stocks</h6>
              </div>
            </div>
            <div className="flex justify-center items-center text-secondary flex-none">
              <Icon16Chevron className="w-4 h-4 md:w-4 md:h-4" />
            </div>
          </div>
        </button>
      </li>
    );
  };

  return (
    <div className={twMerge(clsx('rounded-2xl bg-darkglass py-6', className))}>
      <h4 className="mb-2 pl-6">Categories</h4>
      <div>
        {!isLoading ? (
          <ul className="flex flex-col">
            {ArrayUtils.orEmptyArray(categories).map(renderListItem)}
          </ul>
        ) : (
          <Loader className="mx-auto" />
        )}
      </div>
    </div>
  );
}

export default Categories;
